/** A single region expressed as set membership. E.g. { A: true, B: false } = "only in A". */
export type VennMembership = Readonly<Record<string, boolean>>;

/** Probability configuration for proportional Venn diagram mode. */
export interface VennProbabilities {
  readonly initialPA: number;
  readonly initialPB: number;
  readonly initialPAB: number;
}

/** Input parameters that define the mathematical state of the Venn diagram. */
export interface VennDiagramParams {
  /** Set names to display (order matters for rendering). Default: ['A', 'B'] */
  readonly sets?: string[];
  /** Regions to highlight, each described by membership in each set. Default: [] */
  readonly highlighted?: VennMembership[];
  /** Label for the universal set. Default: 'U' */
  readonly labelU?: string;
  /** When provided, enables proportional mode where circle sizes reflect probabilities. */
  readonly probabilities?: VennProbabilities;
}

/** Visual / display configuration for the Venn diagram. */
export interface VennDiagramDisplayConfig {
  /** Whether hover interaction is enabled. Default: true */
  readonly hoverEnabled?: boolean;
  /** Whether region click events are emitted. Default: false */
  readonly clickEnabled?: boolean;
  /** Highlight colour (CSS colour string). Default: uses CSS variable */
  readonly highlightColor?: string;
  /** Draw circles without overlap (mutually exclusive events). Default: false */
  readonly mutuallyExclusive?: boolean;
  /** Per-set diagonal line shading. Keys are set names, values are CSS colors. */
  readonly setShading?: Readonly<Record<string, string>>;
  /** Clip shading to intersection only (crosshatch only where circles overlap). Default: false */
  readonly shadingIntersectionOnly?: boolean;
  /** Per-set solid tint fill. Keys are set names, values are CSS colors. Rendered at low opacity. */
  readonly setColors?: Readonly<Record<string, string>>;
  /** Shows sliders + computed formula panel. Default: false */
  readonly interactive?: boolean;
  /** Render set labels (A, B, U) at a larger size for small containers. Default: false */
  readonly largeLabels?: boolean;
}

export const DEFAULT_VENN_DIAGRAM_PARAMS: VennDiagramParams = {
  sets: ['A', 'B'],
  highlighted: [],
  labelU: 'U',
};

export const DEFAULT_VENN_DIAGRAM_DISPLAY: VennDiagramDisplayConfig = {
  hoverEnabled: true,
  clickEnabled: false,
};

/**
 * Internal region key used by the SVG template.
 * Not exported — consumers use VennMembership instead.
 */
type InternalRegion = 'onlyA' | 'onlyB' | 'intersection' | 'outside';

/**
 * Converts membership tuples to a set of highlighted internal region keys.
 * The mapping assumes a 2-set diagram whose set names are taken from `params.sets`.
 */
export function resolveHighlightedRegions(params: VennDiagramParams): Set<InternalRegion> {
  const sets = params.sets ?? ['A', 'B'];
  const [setA, setB] = sets;
  const result = new Set<InternalRegion>();

  for (const membership of params.highlighted ?? []) {
    const inA = !!membership[setA];
    const inB = !!membership[setB];

    if (inA && inB) {
      result.add('intersection');
    } else if (inA && !inB) {
      result.add('onlyA');
    } else if (!inA && inB) {
      result.add('onlyB');
    } else {
      result.add('outside');
    }
  }

  return result;
}

/**
 * Converts an internal region key back to a VennMembership tuple.
 */
export function regionToMembership(region: InternalRegion, sets: string[]): VennMembership {
  const [setA, setB] = sets;
  switch (region) {
    case 'onlyA':
      return { [setA]: true, [setB]: false };
    case 'onlyB':
      return { [setA]: false, [setB]: true };
    case 'intersection':
      return { [setA]: true, [setB]: true };
    case 'outside':
      return { [setA]: false, [setB]: false };
  }
}

/** Serialize a membership tuple to a comparable string key (e.g. '110' for {A:T, B:T, C:F}). */
export function membershipKey(membership: VennMembership, sets: string[]): string {
  return sets.map((s) => (membership[s] ? '1' : '0')).join('');
}

/** Build a VennMembership from a string key (e.g. '110' → {A:true, B:true, C:false}). */
export function buildMembership(key: string, sets: string[]): VennMembership {
  const result: Record<string, boolean> = {};
  for (let i = 0; i < sets.length; i++) {
    result[sets[i]] = key[i] === '1';
  }
  return result;
}
